import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { 
  ArrowLeft, 
  CreditCard,
  DollarSign,
  Activity,
  Calendar,
  Gift,
  TrendingUp,
  Crown,
  ShieldCheck,
  User,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Plus
} from 'lucide-react'
import { adminApi } from '@/lib/api'
import { useToast } from '@/components/ui/use-toast'

interface UserDetail {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  role: 'User' | 'Editor' | 'Admin'
  status: 'PENDING_ACTIVATION' | 'ACTIVE' | 'SUSPENDED'
  inviteQuota: number
  createdAt: string
}

interface CreditStats {
  totalCreditsUsed: number
  creditsRemaining: number
  percentageUsed: number
  estimatedCost: number
  actualCost: number
  periodStart: Date
  periodEnd: Date
}

interface BillingCycle {
  id: string
  periodStart: string
  periodEnd: string
  creditLimit: number
  totalCreditsUsed: number
  estimatedCost: number
  actualCost: number
  bonusCredits: number
  intelligenceMode: string
  isCurrent: boolean
  createdAt: string
  activitiesCount: number
}

interface UsageActivity {
  id: string
  serviceType: string
  creditsUsed: number
  estimatedCost: number
  inputTokens?: number
  outputTokens?: number
  createdAt: string
  service?: {
    serviceType: string
    serviceIdentifier: string
    displayName: string
  }
}

interface CreditIncentive {
  id: string
  type: string
  credits: number
  reason: string
  status: string
  expiresAt?: string
  createdAt: string
}

interface UserDetailData {
  user: UserDetail
  subscription?: any
  currentStats: CreditStats | null
  recentLedgers: any[]
  activeIncentives: CreditIncentive[]
  recentTransactions: any[]
}

function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [userDetail, setUserDetail] = useState<UserDetailData | null>(null)
  const [billingHistory, setBillingHistory] = useState<BillingCycle[]>([])
  const [recentActivities, setRecentActivities] = useState<UsageActivity[]>([])
  const [activeTab, setActiveTab] = useState('overview')
  
  // Credit assignment dialog state
  const [creditDialogOpen, setCreditDialogOpen] = useState(false)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditReason, setCreditReason] = useState('')
  const [isBonusCredits, setIsBonusCredits] = useState(false)
  const [assigningCredits, setAssigningCredits] = useState(false)

  useEffect(() => {
    if (userId) {
      loadUserDetail()
    }
  }, [userId])

  const loadUserDetail = async () => {
    if (!userId) return
    
    try {
      setLoading(true)
      
      // Load user details, billing history, and recent activities in parallel
      const [detailResponse, billingResponse, activitiesResponse] = await Promise.all([
        adminApi.getUserCreditDetails(userId),
        adminApi.getUserBillingHistory(userId, { limit: 12 }).catch(() => ({ billingHistory: [] })),
        adminApi.getUserRecentActivities(userId, { limit: 50 }).catch(() => ({ activities: [] }))
      ])
      
      setUserDetail(detailResponse.data)
      setBillingHistory(billingResponse.billingHistory || [])
      setRecentActivities(activitiesResponse.activities || [])
    } catch (error) {
      console.error('Failed to load user detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGoBack = () => {
    const from = location.state?.from || '/users'
    navigate(from, { state: { from: `/users/${userId}`, direction: 'pop' } })
  }

  const handleAssignCredits = async () => {
    if (!userId || !creditAmount || parseFloat(creditAmount) <= 0) {
      toast({
        title: 'Invalid Input',
        description: 'Please enter a valid credit amount greater than 0',
        variant: 'destructive',
      })
      return
    }

    try {
      setAssigningCredits(true)
      
      console.log('Calling adminApi.assignCredits', {
        userId,
        credits: parseFloat(creditAmount),
        reason: creditReason || 'Admin credit assignment',
        isBonus: isBonusCredits,
      });
      
      await adminApi.assignCredits(userId, {
        credits: parseFloat(creditAmount),
        reason: creditReason || 'Admin credit assignment',
        isBonus: isBonusCredits,
      })
      
      console.log('Credit assignment successful', { creditAmount, isBonusCredits });
      
      toast({
        title: 'Credits Assigned',
        description: `Successfully assigned ${creditAmount} ${isBonusCredits ? 'bonus ' : ''}credits to the user`,
      })
      
      // Reset form and close dialog
      setCreditAmount('')
      setCreditReason('')
      setIsBonusCredits(false)
      setCreditDialogOpen(false)
      
      // Reload user details to reflect the change
      console.log('Reloading user details...');
      await loadUserDetail()
      console.log('User details reloaded');
    } catch (error) {
      console.error('Failed to assign credits:', error)
      toast({
        title: 'Error',
        description: 'Failed to assign credits. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setAssigningCredits(false)
    }
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        )
      case 'PENDING_ACTIVATION':
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        )
      case 'SUSPENDED':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Suspended
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  // Get role badge
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'Admin':
        return (
          <Badge variant="default" className="bg-purple-100 text-purple-800">
            <Crown className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        )
      case 'Editor':
        return (
          <Badge variant="outline" className="border-blue-300 text-blue-800">
            <ShieldCheck className="h-3 w-3 mr-1" />
            Editor
          </Badge>
        )
      case 'User':
        return (
          <Badge variant="secondary">
            <User className="h-3 w-3 mr-1" />
            User
          </Badge>
        )
      default:
        return <Badge variant="outline">{role}</Badge>
    }
  }

  const getDisplayName = (user: UserDetail) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }
    return user.email.split('@')[0]
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!userDetail) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">User not found</h3>
              <p className="text-muted-foreground">The requested user could not be found.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { user, currentStats, activeIncentives } = userDetail

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{getDisplayName(user)}</h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <div className="flex space-x-2">
          {getStatusBadge(user.status)}
          {getRoleBadge(user.role)}
        </div>
      </div>

      {/* Stats Cards */}
      {currentStats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Credits Remaining</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentStats.creditsRemaining.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {Number(currentStats.percentageUsed || 0).toFixed(1)}% used this cycle
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Credits Used</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentStats.totalCreditsUsed.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Current billing cycle
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Estimated Cost</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${Number(currentStats.estimatedCost || 0).toFixed(4)}</div>
              <p className="text-xs text-muted-foreground">
                {Number(currentStats.actualCost) > 0 && `Actual: $${Number(currentStats.actualCost).toFixed(4)}`}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Incentives</CardTitle>
              <Gift className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeIncentives.length}</div>
              <p className="text-xs text-muted-foreground">
                Bonus credits active
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detailed Information */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="billing-history">Billing History</TabsTrigger>
          <TabsTrigger value="recent-activities">Recent Activities</TabsTrigger>
          <TabsTrigger value="incentives">Incentives</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>User Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Email:</span>
                  <span>{user.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Name:</span>
                  <span>{getDisplayName(user)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Role:</span>
                  <span>{getRoleBadge(user.role)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Status:</span>
                  <span>{getStatusBadge(user.status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Invite Quota:</span>
                  <span>{user.inviteQuota || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Joined:</span>
                  <span>{new Date(user.createdAt).toLocaleDateString()}</span>
                </div>
              </CardContent>
            </Card>

            {currentStats && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Current Billing Cycle</CardTitle>
                      <CardDescription>
                        {new Date(currentStats.periodStart).toLocaleDateString()} - {new Date(currentStats.periodEnd).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Assign Credits
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Assign Credits</DialogTitle>
                          <DialogDescription>
                            Add credits to this user's current billing cycle
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="credits" className="text-right">
                              Credits
                            </Label>
                            <Input
                              id="credits"
                              type="number"
                              value={creditAmount}
                              onChange={(e) => setCreditAmount(e.target.value)}
                              placeholder="Enter credit amount"
                              className="col-span-3"
                              min="1"
                              step="100"
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="reason" className="text-right">
                              Reason
                            </Label>
                            <Textarea
                              id="reason"
                              value={creditReason}
                              onChange={(e) => setCreditReason(e.target.value)}
                              placeholder="Optional: Reason for credit assignment"
                              className="col-span-3"
                              rows={3}
                            />
                          </div>
                          <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="bonus" className="text-right">
                              Bonus Credits
                            </Label>
                            <div className="col-span-3 flex items-center space-x-2">
                              <Switch
                                id="bonus"
                                checked={isBonusCredits}
                                onCheckedChange={setIsBonusCredits}
                              />
                              <Label htmlFor="bonus" className="font-normal">
                                Add to bonus credit pool (non-expiring)
                              </Label>
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            onClick={() => setCreditDialogOpen(false)}
                            disabled={assigningCredits}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleAssignCredits}
                            disabled={assigningCredits || !creditAmount || parseFloat(creditAmount) <= 0}
                          >
                            {assigningCredits ? 'Assigning...' : 'Assign Credits'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-medium">Credits Used:</span>
                    <span>{currentStats.totalCreditsUsed.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Credits Remaining:</span>
                    <span>{currentStats.creditsRemaining.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Bonus Credits:</span>
                    <span>{(billingHistory.find(c => c.isCurrent)?.bonusCredits || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Usage %:</span>
                    <span>{Number(currentStats.percentageUsed || 0).toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Estimated Cost:</span>
                    <span>${Number(currentStats.estimatedCost || 0).toFixed(4)}</span>
                  </div>
                  {currentStats.actualCost > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium">Actual Cost:</span>
                      <span>${Number(currentStats.actualCost || 0).toFixed(4)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="billing-history">
          <Card>
            <CardHeader>
              <CardTitle>Billing History</CardTitle>
              <CardDescription>Historical billing cycles and credit usage</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead>Credit Limit</TableHead>
                    <TableHead>Credits Used</TableHead>
                    <TableHead>Bonus Credits</TableHead>
                    <TableHead>Estimated Cost</TableHead>
                    <TableHead>Actual Cost</TableHead>
                    <TableHead>Activities</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billingHistory.map((cycle) => (
                    <TableRow key={cycle.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {new Date(cycle.periodStart).toLocaleDateString()} - {new Date(cycle.periodEnd).toLocaleDateString()}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{cycle.creditLimit.toLocaleString()}</TableCell>
                      <TableCell>{cycle.totalCreditsUsed.toLocaleString()}</TableCell>
                      <TableCell>{(cycle.bonusCredits || 0).toLocaleString()}</TableCell>
                      <TableCell>${Number(cycle.estimatedCost || 0).toFixed(4)}</TableCell>
                      <TableCell>${Number(cycle.actualCost || 0).toFixed(4)}</TableCell>
                      <TableCell>{cycle.activitiesCount}</TableCell>
                      <TableCell>
                        {cycle.isCurrent ? (
                          <Badge variant="default">Current</Badge>
                        ) : (
                          <Badge variant="secondary">Completed</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {billingHistory.length === 0 && (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No billing history</h3>
                  <p className="text-muted-foreground">This user has no billing cycles yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recent-activities">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activities</CardTitle>
              <CardDescription>Latest usage activities and credit consumption</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead>Credits Used</TableHead>
                    <TableHead>Estimated Cost</TableHead>
                    <TableHead>Tokens</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentActivities.map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">
                            {activity.service?.displayName || activity.serviceType}
                          </div>
                          {activity.service?.serviceIdentifier && (
                            <div className="text-sm text-muted-foreground">
                              {activity.service.serviceIdentifier}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{activity.creditsUsed.toLocaleString()}</TableCell>
                      <TableCell>${Number(activity.estimatedCost || 0).toFixed(4)}</TableCell>
                      <TableCell>
                        {activity.inputTokens || activity.outputTokens ? (
                          <div className="text-sm">
                            {activity.inputTokens && `${activity.inputTokens.toLocaleString()} in`}
                            {activity.inputTokens && activity.outputTokens && ' / '}
                            {activity.outputTokens && `${activity.outputTokens.toLocaleString()} out`}
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{new Date(activity.createdAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {recentActivities.length === 0 && (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No recent activities</h3>
                  <p className="text-muted-foreground">This user has no recent usage activities.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="incentives">
          <Card>
            <CardHeader>
              <CardTitle>Active Incentives</CardTitle>
              <CardDescription>Bonus credits and special offers</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeIncentives.map((incentive) => (
                    <TableRow key={incentive.id}>
                      <TableCell>
                        <Badge variant="outline">{incentive.type}</Badge>
                      </TableCell>
                      <TableCell>{(incentive.credits || 0).toLocaleString()}</TableCell>
                      <TableCell>{incentive.reason}</TableCell>
                      <TableCell>
                        <Badge variant={incentive.status === 'active' ? 'default' : 'secondary'}>
                          {incentive.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {incentive.expiresAt ? new Date(incentive.expiresAt).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell>{new Date(incentive.createdAt).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {activeIncentives.length === 0 && (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No active incentives</h3>
                  <p className="text-muted-foreground">This user has no active credit incentives.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export { UserDetailPage }
export default UserDetailPage